import React, { useState } from "react";
import StarProgressBar from "@/components/atomics/star-progress-bar";
import StarSendButton from "@/components/atomics/star-send-button";

const MIN_STARS = 1;
const MAX_STARS = 10000;
const SLIDER_MAX = 1000;

interface StarBoostControlProps {
  onSend: (starCount: number) => void;
  isLoading?: boolean;
  buttonText?: string;
  description?: string;
  className?: string;
}

export default function StarBoostControl({
  onSend,
  isLoading = false,
  buttonText = "SEND",
  description = "Choose how many Stars you want to give",
  className = "",
}: StarBoostControlProps) {
  const [starCount, setStarCount] = useState(100);
  const [inputValue, setInputValue] = useState("100");

  const handleSliderChange = (value: number) => {
    setStarCount(value);
    setInputValue(String(value));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;

    // Handle negative numbers - convert to positive
    if (val.startsWith("-")) {
      val = val.substring(1);
    }

    // Allow empty or numeric input
    if (val === "" || /^\d+$/.test(val)) {
      let numVal = parseInt(val, 10);

      // Cap at MAX_STARS if value exceeds it
      if (!isNaN(numVal) && numVal > MAX_STARS) {
        numVal = MAX_STARS;
        val = String(MAX_STARS);
      }

      setInputValue(val);

      if (!isNaN(numVal) && numVal >= MIN_STARS && numVal <= MAX_STARS) {
        setStarCount(numVal);
      }
    }
  };

  const handleInputBlur = () => {
    let numVal = parseInt(inputValue, 10);

    // Handle empty or invalid input
    if (isNaN(numVal) || numVal < MIN_STARS) {
      numVal = MIN_STARS;
    } else if (numVal > MAX_STARS) {
      numVal = MAX_STARS;
    }

    // Convert negative to positive (in case it somehow got through)
    numVal = Math.abs(numVal);

    setStarCount(numVal);
    setInputValue(String(numVal));
  };

  const handleSend = () => {
    onSend(starCount);
  };

  return (
    <main
      className={`flex flex-col justify-center items-center pt-[25px] pb-[30px] pl-[54px] pr-[54px] text-[24px] ${className}`}
    >
      <div className="flex items-center justify-center gap-3">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          className="w-[120px] h-[48px] text-center text-[24px] text-[#FFD900] bg-[#2B2B2B] border-2 border-[#FFD900] rounded-lg font-spline-sans-mono outline-none focus:border-[#0099FE]"
        />
      </div>

      <div className="w-full">
        <StarProgressBar
          value={Math.min(starCount, SLIDER_MAX)}
          setValue={handleSliderChange}
          min={MIN_STARS}
          max={SLIDER_MAX}
        />
        <div className="flex justify-between text-[14px] text-gray-400 font-spline-sans-mono mt-1">
          <span>{MIN_STARS}</span>
          <span>{SLIDER_MAX}+</span>
        </div>
      </div>

      <div className="text-[24px] flex text-center font-spline-sans-mono py-[20px]">
        <span>{description}</span>
      </div>

      <StarSendButton
        className="w-full custom-aspect-320-63 text-[24px] hover:cursor-pointer hover:scale-105 transition-transform duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
        image="/assets/button.svg"
        text={isLoading ? "SENDING..." : buttonText}
        stars={starCount}
        onClick={handleSend}
        disabled={isLoading}
      />
    </main>
  );
}
